import React, { useMemo, useEffect, useRef } from 'react';
import './styles.module.scss';

/**
 * TranscriptDisplay - Read-only display of transcript segments
 * Groups consecutive segments by the same speaker into turns,
 * splits text into sentences with global reference numbers for citation linking.
 * @param {Array} segments - transcript segments
 * @param {boolean} loading
 * @param {{ start: number, end: number }|number|null} highlightRef - sentence ref or range to highlight
 * @param {object|null} transcriptReferences - backend-provided sentence map (used for sentence boundaries)
 */
export default function TranscriptDisplay({ segments, loading, highlightRef: highlightSentenceRaw, transcriptReferences }) {
  const highlightElRef = useRef(null);

  // Normalize highlight into { start, end } or null
  const highlightRange = useMemo(() => {
    if (!highlightSentenceRaw) return null;
    if (typeof highlightSentenceRaw === 'number') {
      return { start: highlightSentenceRaw, end: highlightSentenceRaw };
    }
    if (highlightSentenceRaw.start != null) {
      return { start: highlightSentenceRaw.start, end: highlightSentenceRaw.end || highlightSentenceRaw.start };
    }
    return null;
  }, [highlightSentenceRaw]);

  const isSentenceHighlighted = (num) => {
    if (!highlightRange) return false;
    return num >= highlightRange.start && num <= highlightRange.end;
  };

  // Split text into sentences (mirrors backend _split_sentences)
  const splitSentences = (text) => {
    const parts = text.split(/(?<=[.!?])\s+(?=[A-Z"'])/);
    return parts.map(p => p.trim()).filter(Boolean);
  };

  // Group segments into turns and split into sentences with global numbering
  const turnsWithSentences = useMemo(() => {
    if (!segments || segments.length === 0) return { turns: [], totalSentences: 0 };

    // Group into turns
    const rawTurns = [];
    let currentTurn = null;
    segments.forEach((segment) => {
      const speaker = segment.speaker || 'UNKNOWN';
      const text = segment.text || '';
      const start = segment.start || segment.start_time || 0;
      const end = segment.end || segment.end_time || start;

      if (!currentTurn || currentTurn.speaker !== speaker) {
        if (currentTurn) rawTurns.push(currentTurn);
        currentTurn = { speaker, texts: [text], start, end };
      } else {
        currentTurn.texts.push(text);
        currentTurn.end = end;
      }
    });
    if (currentTurn) rawTurns.push(currentTurn);

    // If we have backend transcript_references, use those sentence boundaries
    // Otherwise, split sentences ourselves
    let sentenceCounter = 0;
    const turns = rawTurns.map((turn) => {
      const fullText = turn.texts.join(' ');
      let sentences;

      if (transcriptReferences) {
        // Collect sentences from references that belong to this turn
        const turnSentences = [];
        let nextRef = sentenceCounter + 1;
        while (transcriptReferences[String(nextRef)] &&
               transcriptReferences[String(nextRef)].turn_index !== undefined) {
          const ref = transcriptReferences[String(nextRef)];
          // Check if this ref's turn_index matches by comparing speaker + approximate timing
          if (ref.speaker === turn.speaker) {
            turnSentences.push({ num: nextRef, text: ref.sentence || ref.text_preview });
            nextRef++;
          } else {
            break;
          }
        }
        if (turnSentences.length > 0) {
          sentenceCounter = nextRef - 1;
          sentences = turnSentences;
        } else {
          // Fallback to local splitting
          const localSents = splitSentences(fullText);
          sentences = localSents.map((s) => {
            sentenceCounter++;
            return { num: sentenceCounter, text: s };
          });
        }
      } else {
        const localSents = splitSentences(fullText);
        sentences = localSents.map((s) => {
          sentenceCounter++;
          return { num: sentenceCounter, text: s };
        });
      }

      return { ...turn, sentences, fullText };
    });

    return { turns, totalSentences: sentenceCounter };
  }, [segments, transcriptReferences]);

  // Scroll first highlighted sentence into view
  useEffect(() => {
    if (highlightRange && highlightElRef.current) {
      highlightElRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading transcript...</span>
      </div>
    );
  }

  if (!segments || segments.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-2">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-500 text-sm">No transcript available yet</p>
      </div>
    );
  }

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerColor = (speaker) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-teal-100 text-teal-800 border-teal-200',
      'bg-red-100 text-red-800 border-red-200',
    ];
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const { turns } = turnsWithSentences;

  return (
    <div className="space-y-1">
      {turns.map((turn, turnIdx) => {
        const colorClass = getSpeakerColor(turn.speaker);
        const hasSentenceHighlight = turn.sentences.some(s => isSentenceHighlighted(s.num));

        return (
          <div
            key={`turn-${turnIdx}`}
            className={`group flex gap-3 py-3 px-4 rounded-lg transition-all duration-300 ${
              hasSentenceHighlight ? 'bg-yellow-50/50' : 'hover:bg-gray-50'
            }`}
          >
            {/* Timestamp */}
            <div className="flex-shrink-0 w-16 pt-0.5">
              <span className="text-xs font-mono text-gray-400">
                {formatTime(turn.start)}
              </span>
            </div>

            {/* Speaker */}
            <div className="flex-shrink-0 w-32 pt-0.5">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
                {turn.speaker}
              </span>
            </div>

            {/* Sentences */}
            <div className="flex-1 min-w-0">
              {turn.sentences.map((sent) => {
                const isHighlighted = isSentenceHighlighted(sent.num);
                const isFirstHighlighted = isHighlighted && highlightRange && sent.num === highlightRange.start;
                return (
                  <span
                    key={`ref-${sent.num}`}
                    id={`ref-${sent.num}`}
                    ref={isFirstHighlighted ? highlightElRef : null}
                    className={`inline transition-all duration-500 ${
                      isHighlighted
                        ? 'bg-yellow-200 ring-1 ring-yellow-400 rounded px-0.5 -mx-0.5'
                        : ''
                    }`}
                  >
                    <span className="text-sm text-gray-800 leading-relaxed">{sent.text} </span>
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
